// Zod contracts for Marketing › New Client Offer › Overview (funnel, sources, cost per new client, who needs a
// nudge) and › Guests (status-filtered list + one guest's timeline).
import { z } from 'zod';
import { CAMPAIGN_TRAFFIC_SOURCES } from '../campaign-enums';
import { ANALYTICS_RANGE_PRESETS } from '../funnel-attribution';
import {
  offerClaimEventTypeSchema,
  offerClaimStatusSchema,
  offerMessageStepSchema,
  offerNotEligibleReasonSchema,
  offerRedeemModeSchema,
  offerRedemptionMethodSchema,
  offerSignupChannelSchema,
  offerTimestampSchema,
} from './new-client-offer-schemas';
import { offerUndoBlockedReasonSchema } from './new-client-offer-staff-schemas';

const count = z.number().int().min(0);
const cents = z.number().int().min(0);

/**
 * Counts for a cohort of guests (by sign-up date), each out of `signedUp` (not nested: a walk-in uses the offer
 * without booking). `signedUp` = new guests whose first sign-up was eligible (existing clients turned away are not
 * in it); `bookedAhead` = their offer sits on (or was used on) a visit that was not a walk-in — a released or
 * no-show visit is not booked; `cameIn` = "Used" — REDEEMED, the visit was marked Done.
 */
export const offerFunnelCountsSchema = z.object({
  signedUp: count,
  bookedAhead: count,
  /** @deprecated Same as `bookedAhead` (kept for web builds that predate it). */
  booked: count,
  cameIn: count,
});
export type OfferFunnelCounts = z.infer<typeof offerFunnelCountsSchema>;

export const offerOverviewKpisSchema = offerFunnelCountsSchema.extend({
  /**
   * Checked in (kiosk, QR, text link or front desk) in this claim period, or used the offer — between Booked
   * and Used. Optional: older API builds don't send it, and the web then hides the step.
   */
  checkedIn: count.optional(),
  /**
   * `signedUp` who filled in the offer form (a page, a friend's or a creator's link). Leads lists these plus the
   * `turnedAway` (they filled in the form too), each dated by its sign-up rather than the claim's current window.
   */
  signedUpOnline: count,
  /** `signedUp` who joined in the salon (self check-in, counter QR, added by staff): no form, so not on Leads. */
  joinedInSalon: count,
  /** Existing clients who filled in the form and were turned away — never part of `signedUp`. */
  turnedAway: count,
  /**
   * @deprecated Always 0: older web builds print it as "incl. N not eligible" under Signed up, which no longer holds.
   * Read `turnedAway`.
   */
  notEligible: count,
  /** Used without a booked-ahead visit (walked in, or used at the counter). */
  walkedIn: count,
  /** `signedUp` who tapped holding the offer and never used it. Not part of `cameIn`, which means used. Older API builds don't send it. */
  cameInWithTap: count.optional(),
  /** Ad spend in the range of the ad campaigns that sent guests to the offer. */
  adSpendCents: cents,
  /** Guests from those ad campaigns who used the offer. */
  adCameIn: count,
  /** adSpendCents ÷ adCameIn; null without spend or without anyone coming in yet. */
  costPerNewClientCents: cents.nullable(),
});

/** One part of a source row ("← Instagram" · 5, "Meta ads" · 7, "Vy Nguyen" · 2), busiest first. */
export const offerOverviewSourcePartSchema = z.object({ label: z.string(), signedUp: count });
export type OfferOverviewSourcePart = z.infer<typeof offerOverviewSourcePartSchema>;

export const offerOverviewSourceSchema = z.object({
  /** `offerSourceBucketKey()` — also the Guests list `source` filter (`parseOfferSourceBucketKey`). */
  key: z.string(),
  /** `offerSourceBucketLabel()`. */
  label: z.string(),
  /** @deprecated Read the bucket from `key`; 'campaign' for a campaign row, 'placement' for every other row. */
  kind: z.enum(['campaign', 'placement']),
  campaignId: z.string().nullable(),
  /** @deprecated Always null: a campaign's declared traffic source is never where its guests came from. */
  trafficSource: z.enum(CAMPAIGN_TRAFFIC_SOURCES).nullable(),
  /** @deprecated Always null: rows are source buckets, their parts say where guests came from. */
  channel: offerSignupChannelSchema.nullable(),
  /** Joined in the salon (no form) — the Overview groups these under "Joined in the salon". */
  inSalon: z.boolean(),
  /** Empty for a row with no breakdown (friend referral, in-salon rows). */
  parts: z.array(offerOverviewSourcePartSchema),
  signedUp: count,
  bookedAhead: count,
  /** @deprecated Same as `bookedAhead`. */
  booked: count,
  cameIn: count,
  /** Campaign rows only (null = organic); 0 = an ad campaign with no spend entered for the range. */
  adSpendCents: cents.nullable(),
  costPerNewClientCents: cents.nullable(),
});
export type OfferOverviewSource = z.infer<typeof offerOverviewSourceSchema>;

/**
 * The "Needs a nudge" groups, also a Guests list filter so each row opens exactly the guests it counts:
 * `not_booked` = open pass, not past expiry · `expiring_soon` = open or missed-visit pass expiring within
 * `OFFER_EXPIRING_SOON_DAYS` · `no_show` · `expired` = ran out unused inside the selected range. A guest who tapped
 * holding the pass since it opened came in and gets no more nudge texts, so `not_booked`, `expiring_soon` and `no_show`
 * leave them out.
 */
export const OFFER_GUEST_NUDGES = ['not_booked', 'expiring_soon', 'no_show', 'expired'] as const;
export type OfferGuestNudge = (typeof OFFER_GUEST_NUDGES)[number];
export const offerGuestNudgeSchema = z.enum(OFFER_GUEST_NUDGES);
export const OFFER_EXPIRING_SOON_DAYS = 3;

export const OFFER_GUEST_NUDGE_LABELS: Record<OfferGuestNudge, string> = {
  not_booked: 'Not booked yet',
  expiring_soon: `Expiring in ${OFFER_EXPIRING_SOON_DAYS} days`,
  no_show: 'No-show',
  expired: 'Expired unused',
};

/** Current state (not the range), except `expiredInRange`. */
export const offerNeedsNudgeSchema = z.object({
  /** Open passes, nobody booked yet — the reminders are running. */
  notBookedYet: count,
  /** Open (or missed-visit) passes expiring in the next 3 days. */
  expiringSoon: count,
  noShow: count,
  /** Passes that ran out unused inside the selected range. */
  expiredInRange: count,
});

export const offerOverviewSchema = z.object({
  range: z.enum(ANALYTICS_RANGE_PRESETS),
  kpis: offerOverviewKpisSchema,
  /** The same counts for the equally long period just before the range; null for "All time". */
  previous: offerFunnelCountsSchema.nullable(),
  /** Busiest first. Ad campaigns that use the offer but had no sign-ups still show (with their spend). */
  sources: z.array(offerOverviewSourceSchema),
  needsNudge: offerNeedsNudgeSchema,
  /** Offer texts waiting to go out now (current state, not the range) that the send-time checks will still send. */
  textsQueued: count,
  /**
   * Queued offer texts the send-time checks will skip when they come due (the guest booked, texted back, opted
   * out, the pass expired…) — never counted as queued.
   */
  textsWillSkip: count,
  /**
   * Hayven Online Booking is on. Off = most bookings happen outside Hayven, so unless staff enter some in the
   * Hayven calendar "Booked" only mirrors "Came in" — the web then hides it and an open pass reads "Not in yet".
   */
  hayvenBookingEnabled: z.boolean(),
  /**
   * How guests use the offer now (`CHECK_IN` / `COUNTER_QR`). At a counter-QR salon (check-in off) Hayven sees no
   * visits, so the web shows only Signed up → Used. Older API builds don't send it (read as `CHECK_IN`).
   */
  redeemMode: offerRedeemModeSchema.optional(),
  /**
   * The salon's bookings live in Hayven (Calendar on), with or without Hayven Check-in: "Booked" is real even at a
   * counter-QR salon. False = they live on its POS. Older API builds don't send it.
   */
  bookingsInHayven: z.boolean().optional(),
});
export type OfferOverview = z.infer<typeof offerOverviewSchema>;

// ---------------------------------------------------------------------------
// Guests
// ---------------------------------------------------------------------------

export const OFFER_GUESTS_PAGE_SIZE = 50;

/**
 * Why the offer's reminder texts no longer go to a guest: the offer was used, a visit is booked, the pass ran out,
 * the guest opted out of texts (STOP / revoked), never agreed to marketing texts (e.g. joined in the salon),
 * texted the salon back (a person took over), or came in on a tap that held the pass since it opened. Null while
 * reminders run. A next text can still be set alongside it (a booked guest's pass, a returning guest's welcome back).
 */
export const OFFER_TEXTS_STOPPED_REASONS = ['used', 'booked', 'expired', 'opted_out', 'no_consent', 'replied', 'came_in'] as const;
export type OfferTextsStoppedReason = (typeof OFFER_TEXTS_STOPPED_REASONS)[number];
export const offerTextsStoppedReasonSchema = z.enum(OFFER_TEXTS_STOPPED_REASONS);

/** The next offer text that will really go out to the guest (one the send-time checks would skip is left out). */
export const offerGuestNextTextSchema = z.object({ step: offerMessageStepSchema, at: offerTimestampSchema });
export type OfferGuestNextText = z.infer<typeof offerGuestNextTextSchema>;

export const offerGuestRowSchema = z.object({
  /** Claim id — the drawer key and the cursor. */
  id: z.string(),
  clientId: z.string(),
  name: z.string(),
  /** Full number with contact access, last 4 only ("••• 3133") without it. */
  phoneDisplay: z.string().nullable(),
  /** E.164 for Text / Call; null without contact access (or no usable phone). */
  phoneE164: z.string().nullable(),
  /** Texted STOP: Call only. Always false without contact access. */
  optedOutViaStop: z.boolean(),
  status: offerClaimStatusSchema,
  /** `offerGuestSourceLabel()` of the sign-up that opened the claim's current window. */
  sourceLabel: z.string(),
  /** One line on where the guest is now ("Reminder 1 sent Oct 2 · expires Oct 15"). */
  latestLabel: z.string(),
  claimedAt: offerTimestampSchema,
  expiresAt: offerTimestampSchema,
  /** Creator links: the beauty creator this guest is credited to ("maibeauty", shown "@maibeauty"); null when none. */
  creatorHandle: z.string().nullable(),
  /** "Next text: Reminder 2 · Oct 9, 10 AM"; null when nothing more will go out. */
  nextText: offerGuestNextTextSchema.nullable(),
  /** Why reminders stopped ("Stopped: replied"); null while they still run (or never applied). */
  stoppedReason: offerTextsStoppedReasonSchema.nullable(),
  /** A pass still open or booked that a live tap held since it opened: `status` stays, as the guest can still use it, but they came in. */
  cameInWithTap: z.boolean().optional(),
});
export type OfferGuestRow = z.infer<typeof offerGuestRowSchema>;

/**
 * Per-status totals for the filter chips (search + source applied, status not). `all` = the Overview's Signed up
 * rule: existing clients turned away at sign-up are left out of it (and of the unfiltered list) but still counted
 * and listed under NOT_ELIGIBLE, so the status chips sum to `all` + those turned away.
 */
export const offerGuestStatusCountsSchema = z.object({
  all: count,
  ACTIVE: count,
  BOOKED: count,
  NO_SHOW: count,
  REDEEMED: count,
  EXPIRED: count,
  NOT_ELIGIBLE: count,
});
export type OfferGuestStatusCounts = z.infer<typeof offerGuestStatusCountsSchema>;

export const offerGuestListSchema = z.object({
  items: z.array(offerGuestRowSchema),
  nextCursor: z.string().nullable(),
  counts: offerGuestStatusCountsSchema,
  /** Label of the `source` filter when one is applied ("Bio Page", "Campaign · Fall promo"), for the filter chip. */
  sourceLabel: z.string().nullable(),
});
export type OfferGuestList = z.infer<typeof offerGuestListSchema>;

/** A visit in a guest's story: when, and "Gel Manicure · Linh". */
export const offerGuestVisitSchema = z.object({
  id: z.string(),
  startTime: offerTimestampSchema,
  summary: z.string(),
});
export type OfferGuestVisit = z.infer<typeof offerGuestVisitSchema>;

export const offerGuestEventSchema = z.object({
  id: z.string(),
  type: offerClaimEventTypeSchema,
  occurredAt: offerTimestampSchema,
  /** `offerEventLabel()` title / detail. */
  title: z.string(),
  detail: z.string().nullable(),
  /** Staff member who acted; null for the guest or the system. */
  actorName: z.string().nullable(),
  /** The visit the event was about (null when none, or the appointment was deleted). */
  appointment: offerGuestVisitSchema.nullable(),
});
export type OfferGuestEvent = z.infer<typeof offerGuestEventSchema>;

export const offerGuestDetailSchema = offerGuestRowSchema.extend({
  /** Where the guest's very first sign-up came from, when the current window was opened by a later one. */
  firstCameFrom: z.object({ label: z.string(), at: offerTimestampSchema }).nullable(),
  notEligibleReason: offerNotEligibleReasonSchema.nullable(),
  /** "15% OFF" — the discount snapshot the guest was promised. */
  label: z.string(),
  headline: z.string(),
  /** The visit holding the claim (BOOKED / NO_SHOW) or the one that used it (REDEEMED). */
  appointment: offerGuestVisitSchema.nullable(),
  redeemedAt: offerTimestampSchema.nullable(),
  redemptionMethod: offerRedemptionMethodSchema.nullable(),
  redeemedByName: z.string().nullable(),
  /** Set when a used offer cannot be undone from the app (see `offerUndoBlockedReasonSchema`). */
  undoBlockedReason: offerUndoBlockedReasonSchema.nullable(),
  /** Oldest first. */
  events: z.array(offerGuestEventSchema),
});
export type OfferGuestDetail = z.infer<typeof offerGuestDetailSchema>;

/** The offer a lead's person holds, for the Leads row chip + drawer row. */
export const leadOfferSchema = z.object({
  claimId: z.string(),
  status: offerClaimStatusSchema,
  /** "15% OFF". */
  label: z.string(),
  /** Same as a guest row's. */
  cameInWithTap: z.boolean().optional(),
});
export type LeadOffer = z.infer<typeof leadOfferSchema>;
